import { share, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
// Angular JWT libary used for authentication
import { Router } from '@angular/router';

@Injectable()
export class RequestService {
  /**
   * ====================================
   *   REQUEST SERVICE
   * ====================================
   *
   * The request service is the only service that actually handles request to the server.
   * What this means is that all the other services uses the request method to retrieve their data
   * from server.
   *
   * The class has two methods:
   *
   * 1. Public request which handles all the requests to server
   *
   *    @Params: method  : string
   *            url     : string
   *            key     : string (Optional)
   *            data    : any (Optional)
   *
   *    @Returns: Observable
   *
   *
   * 2. Public invalidate which invalidates cached data based in a key
   *
   *    @Params: key     : string
   *
   *    @Returns: void
   *
   */

  // Information watchers
  private _watchers: any[];

  // Dictionary that stores all the data related to jobs
  private _cachedData: {};

  // Variable to hold default headers
  private _header: HttpHeaders;

  // Pagination paths
  private _paginationPaths: {};

  constructor(private http: HttpClient, private _router: Router) {
    /**
     * ====================================
     *   DEFAULT HEADERS
     * ====================================
     *
     * These headers are used for all post requests as of today
     *
     */

    this._header = new HttpHeaders();
    this._header.append('Content-Type', 'application/json');

    this._watchers = [];
    this._paginationPaths = {};

    /**
     * The dict is initialized as a empty dict and later filled when different
     * requests are initiated
     */

    this._cachedData = {};

    /**
     * If the path changes, we need to reset the pagination paths
     */

    this._router.events.subscribe(val => {
      this._paginationPaths = {};
    });
  }

  static getHeadersWithoutAuth() {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
  }

  /**
   * =======================================================
   *   MAIN REQUEST METHOD
   * =======================================================
   */

  public getHeaders(isMultipart: boolean = false) {
    let headers = new HttpHeaders();
    if (localStorage.getItem('id_token')) {
      headers = new HttpHeaders({
        Accept: 'application/json',
        Authorization: 'JWT ' + localStorage.getItem('id_token'),
      });
    } else {
      headers = new HttpHeaders({
        Accept: 'application/json',
      });
    }
    if (!isMultipart) {
      headers = headers.set('Content-Type', 'application/json');
    }
    return headers;
  }

  public request<T extends Object>(
    method: string,
    url: string,
    key?: string,
    data?: any
  ): Observable<T> {
    /**
     * This method is used to handle all types of requests, and does caching on client
     * side based on different keys.
     */

    // If cache exist the data is returned as an observable object
    if (key && this._cachedData[key] && method !== 'POST') {
      return new Observable(observer => {
        observer.next(this._cachedData[key]);
        observer.complete();
      });
    }

    /**
     *
     * What class we want to use to generate requests to the server depends on
     * if the user is logged in or not. To check that a user is logged in, we
     * check if there are a token stored in the local storage.
     *
     */

    // Declares a variable to be initialized with the correct class
    const http = this.http;

    /**
     * If the method continues to this point, it means that no cached values was found
     * and that we need to request new information from the server
     */

    const headers = this.getHeaders();

    switch (method) {
      case 'POST': // The method is of type post
        return http.post<T>(url, JSON.stringify(data), { headers });

      case 'PUT': // The method is of type put
        return http.put<T>(url, JSON.stringify(data), { headers });

      case 'DELETE': // The method is of type delete
        return http.delete<T>(url, { headers });

      case 'GET': // The method is of type get
        return http.get<T>(url, { headers }).pipe(
          share(),
          tap(res => {
            // The value is saved in the cache dict if key is provided
            if (key) {
              this._cachedData[key] = res;
            }
          })
        );
    }
  }

  /**
   * =======================================================
   *   INVALIDATION METHOD
   * =======================================================
   */

  public invalidate(key): void {
    /**
     *
     * This method is used every time the cached data needs to be
     * invalidated. The invalidation happens based on a provided key
     *
     */

    delete this._cachedData[key];
  }

  /**
   * =======================================================
   *   INVALIDATE ALL METHOD
   * =======================================================
   */

  public invalidateAll(): void {
    /**
     *
     * This method is used to invalidate all cached data. Typically used when a user loggs out.
     *
     */

    this._cachedData = {};
  }

  /**
   * =======================================================
   *   REGISTER WATCHER
   * =======================================================
   */

  public registerWatcher(watcher: any) {
    /**
     *
     * Register watchers adds a new watcher to the watchers list
     *
     */

    // Pushes a new watcher to the list
    this._watchers.push(watcher);
  }

  /**
   * =======================================================
   *   ALERT WATCHERS
   * =======================================================
   */

  public alertWatchers() {
    /**
     *
     * AlertComponent watchers iterates through all the registered wathcers and alerts
     * them about that something has changed.
     *
     */

    this._watchers.forEach((watcher: any) => {
      // Calls update method on each watcher
      watcher.alertUpdate();
    });
  }

  /**
   * =======================================================
   *   SET & GET PAGINATION PATH
   * =======================================================
   */

  public setPaginationPath(key, path): void {
    this._paginationPaths[`${key}`] = path;
  }

  public getPaginationPath(key): string {
    return this._paginationPaths[`${key}`];
  }

  public clearPaginationPath(key): void {
    delete this._paginationPaths[`${key}`];
  }

  public clearPaginationPaths(): void {
    this._paginationPaths = {};
  }
}
